import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supplierService } from '@services';
import { CreateSupplierRequest } from '@app-types';
import { GenericForm, FormField } from '@components/common/forms';
import { Textarea } from '@components/common';
import { getApiErrorMessage, splitApiFieldErrors } from '@utils/apiError';

interface CreateSupplierFormProps {
  onSubmit: (data: CreateSupplierRequest, note: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error?: unknown;
}

export const CreateSupplierForm: React.FC<CreateSupplierFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  error
}) => {
  const [note, setNote] = useState('');

  // Fetch tiers for dropdown
  const { data: tiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['supplierTiers'],
    queryFn: () => supplierService.getSupplierTiers()
  });

  // Define form fields
  const createSupplierFormFields: FormField[] = [
    {
      name: 'vendor_id',
      label: 'Supplier Number',
      type: 'text',
      required: true,
      placeholder: 'e.g., SUP-001',
      validation: async (value: unknown) => {
        const strValue = String(value || '');
        if (!strValue.trim()) {
          return 'Supplier Number is required';
        }

        // Check uniqueness with backend
        try {
          const exists = await supplierService.checkVendorId(strValue.trim());
          if (exists) {
            return 'This Supplier Number already exists';
          }
        } catch (error) {
          console.error('Error checking vendor ID:', error);
          return 'Error checking Supplier Number availability';
        }

        return undefined;
      }
    },
    {
      name: 'name',
      label: 'Supplier Name',
      type: 'text',
      required: true,
      placeholder: 'Enter supplier name'
    },
    {
      name: 'tier_id',
      label: 'Tier',
      type: 'select',
      required: true,
      disabled: tiersLoading,
      options: [
        { value: '', label: 'Select Tier' },
        ...(tiers?.map((tier) => ({
          value: tier.id.toString(),
          label: tier.name
        })) || [])
      ]
    },
    {
      name: 'high_trust_supplier',
      label: '',
      type: 'checkbox',
      placeholder: 'High Trust Supplier'
    },
    {
      name: 'critical_service_product',
      label: '',
      type: 'checkbox',
      placeholder: 'Critical Service/Product'
    }
  ];

  const handleSubmit = async (data: Record<string, string | string[] | boolean>) => {
    // Convert tier_id from string to number
    const payload: CreateSupplierRequest = {
      vendor_id: data.vendor_id as string,
      name: data.name as string,
      tier_id: Number.parseInt(data.tier_id as string, 10),
      high_trust_supplier: (data.high_trust_supplier as boolean) || false,
      critical_service_product: (data.critical_service_product as boolean) || false
    };

    onSubmit(payload, note);
  };

  const { fieldErrors, residualMessages } = error
    ? splitApiFieldErrors(error, ['vendor_id', 'name', 'tier_id'])
    : { fieldErrors: {}, residualMessages: [] };
  const showBanner = residualMessages.length > 0 || (Boolean(error) && Object.keys(fieldErrors).length === 0);

  return (
    <>
      {showBanner && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626'
          }}
        >
          <strong>Error:</strong>{' '}
          {residualMessages.join(' ') || getApiErrorMessage(error, 'Failed to create supplier. Please try again.')}
        </div>
      )}

      <div className="readonly-field" style={{ marginBottom: '16px' }}>
        <strong>Note:</strong> Supplier Number cannot be changed after creation. Status will be automatically set to
        "Inactive" and calculated by the system based on contracts.
      </div>

      <GenericForm
        fields={createSupplierFormFields}
        initialData={{
          high_trust_supplier: false,
          critical_service_product: false
        }}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        submitLabel="Create Supplier"
        externalErrors={fieldErrors}
      >
        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optionally record the reason or context for creating this supplier"
          rows={3}
          dataTestId="supplier-note"
        />
      </GenericForm>
    </>
  );
};
